import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { DateTime, Duration, DurationUnit } from 'luxon';
import { Observable, OperatorFunction, from, lastValueFrom, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { DateRange } from 'src/app/common/daterange';
import { AuthState } from 'src/app/store/auth/auth.state';
import { utcToLocalISO } from 'src/app/util/date-helpers';
import { IntegrationData, IntegrationId, PagingObject, StringLiteral, TimeuseEntityType, TimeuseScore, WorkLife } from 'src/models';
import { ApiLimitParams, ApiService } from '../api.service';
import { PagedApiResult, PagedResult, PagingService } from '../paging/paging.service';
import { UserService } from '../user/user.service';

const MODE_RATIO_LEAVE_FIELDS = 'paidLeave,unpaidLeave';
const TIMESHEET_MODE_RATIO_FIELDS = [
  'mobile', 'manual', 'userId', 'offcomputer', 'computer', 'computerRatio',
  'paidBreak', 'paidBreakRatio', 'unpaidBreak', 'unpaidBreakRatio',
  'meeting', 'meetingRatio', MODE_RATIO_LEAVE_FIELDS,
].join(',');


export type StatsUserFilter = 'all-self-on-reports' | 'all-on-reports' | StringLiteral | string[];
export type GetStatsPeriod = DurationUnit;
export interface StatsFilter {
  user?: StatsUserFilter;
  tag?: string | string[];
}

export interface StatsTotal {
  id: string;
  total: number;
  userId: string;
  name: string;
  project?: string;
  integration?: IntegrationId;
  integrationData?: IntegrationData;
  date?: string;
  pagingTotalCount?: number;
  projectName?: string;
}

export interface CategoryTotal {
  id: string;
  total: number;
  entity: TimeuseEntityType;
  name: string;
  score: TimeuseScore;
}

export interface ScoreRatio {
  [TimeuseScore.Unrated]: number;
  [TimeuseScore.Unproductive]: number;
  [TimeuseScore.Neutral]: number;
  [TimeuseScore.Productive]: number;
  userId: string;
  partial: number;
  total: number;
  null: number;
  ratio: number;
}

export interface ModeRatio {
  computer: number;
  manual: number;
  mobile: number;
  offcomputer: number;
  partial: number;
  ratio: number;
  total: number;
  userId: string;
  count?: number;
  paidBreak?: number;
  unpaidBreak?: number;
  paidLeave?: number;
  unpaidLeave?: number;
  meeting?: number;
  meetingRatio?: number;
}

export interface ModeRatioExtended extends ModeRatio {
  computerRatio: number;
  manualRatio: number;
  mobileRatio: number;
  offcomputerRatio: number;
  paidBreakRatio?: number;
}

export interface SummaryRatio {
  userId: string;
  total: number;
  idleMinsRatio: number;
  idleSecRatio: number;
  prod: number;
  prodRatio: number;
  unprod: number;
  unprodRatio: number;
  manual: number;
  manualRatio: number;
  mobile: number;
  mobileRatio: number;
  keysPerNonIdleMin: number;
  idleMins: number;
  idleSec: number;
  count?: number;
  paidBreak?: number;
  paidBreakRatio?: number;
  modeTotal?: number;
  scoreTotal?: number;
  activityLevel?: number;
  unrated?: number;
  paidLeave?: number;
  meeting?: number;
  meetingRatio?: number;
  test?: number;
  meetingCount?: number;
  avgMeetingDuration?: number;
  avgIdleTimePerMeeting?: number;
  meetingDelta?: number;
  meetingCountDelta?: number;
  avgMeetingDurationDelta?: number;
  avgIdleTimePerMeetingDelta?: number;
}

export interface Summary {
  date?: string;
  userId: string;
  userName?: string;
  userArchived?: boolean;
  total: number;
  idleMinsRatio: number;
  idleSecRatio: number;
  ratio: number;
  ratioTotal: number;
  manual: number;
  manualRatio: number;
  mobile: number;
  mobileRatio: number;
  idleMins: number;
  idleSec: number;
  modeTotal: number;
  activeBiasRatio: number;
  unprod?: number;
  unprodRatio?: number;
  paidBreak?: number;
  paidBreakRatio?: number;
  unpaidBreak?: number;
  paidLeave?: number;
  meeting?: number;
  meetingRatio?: number;
  diffFromAvgActivity?: number;
  activityLevel?: number;
  test?: number;
}

export interface RawRatio {
  clicks: number;
  keys: number;
  moves: number;
  idleSec: number;
  idleMins: number;
  totalSec: number;
  totalMins: number;
  userId: string;
}

export interface WorkRange {
  userId: string;
  start: string;
  end: string;
  workRange: number;
  total: number;
  date: string;
}

export interface GetStatsOptions extends ApiLimitParams, StatsFilter {
  // ID of Company or Workspace, user refers to.
  company?: string;

  // ISO Date of range beginning (inclusive); (`to` minus 30mins) by default
  from?: string;

  // ISO Date of range ending (exclusive); current by default
  to?: string;

  // company timezone
  timezone?: string;
  fields?: string;
  count?: string;
  'group-by'?: string | number;
  sort?: string;
  mode?: string;
  aggr?: 0 | 1;

  // API cache time in seconds
  'api-cache'?: number;
  'api-cache-ignore'?: string;


  /** @deprecated Use category-total API */
  realtime?: 0 | 1;
  'no-total'?: 0 | 1;
  interval?: number;
  period?: GetStatsPeriod;
  'period-start'?: string;
  'interval-bias'?: number;
  resolve?: string;

  'filter[total]'?: string;
  'filter[partial]'?: string;
  'include-archived-users'?: 0 | 1;
  'bottom-limit'?: number;
  breaks?: 'all' | 'paid' | 'unpaid';
  unpaid?: 0 | 1;
  paging?: 0 | 1;
  'date-paging'?: 0 | 1;
  'date-limit'?: number;
  'paging-fields'?: string;
  untracked?: 0 | 1;
  'untracked-dates'?: 0 | 1;
  'include-future'?: 0 | 1;
  project?: string;
  task?: string;
}

type GetStatsOptionsNormalized = Omit<GetStatsOptions, 'user' | 'tag'> & { user?: string, tag?: string };

export interface NewStatsOptions extends GetStatsOptions {
  resolve?: string;
  level?: 'user' | 'company';
  'ratio-limit'?: number;
  'ratio-field'?: string;
  'ratio-page'?: string;
  'ratio-sort'?: string;
  ratio?: string;
  'ratio-id'?: TimeuseScore;
  'prefilter[score]'?: string | number | string[] | number[];
  'prefilter[comCat]'?: string;
  'filter[activeBiasRatio]'?: string;
}

export interface NewStatsUserResult {
  ratio: number;
  ratioCount?: number;
  total: number;
  userId: string;
  userName?: string;
  userArchived?: boolean;
  currentTagId?: string;
  currentTagName?: string;
  score?: {
    id: string;
    total: number;
    ratio: number;
  }[];
}

export interface NewStatsComcatResult {
  comCat: string;
  entity: 'domain' | 'app';
  name: string;
  score: TimeuseScore;
  total: number;
  userCat?: string[];
  userId?: string;
  count?: number;
}

export interface NewStatsCompanyResult {
  ratioCount?: number;
  score?: {
    id: string;
    total: number;
    ratio: number;
  }[];
  total?: number;
  ratio?: number;
  count?: number;
}

export interface NewStatsActivity extends NewStatsComcatResult {
  clicks: number;
  keys: number;
  moves: number;
  idleSec: number;
  idleSecRatio?: number;
  idleMins: number;
  idleMinsRatio?: number;
  totalSec: number;
  totalMins: number;
  taskId?: string;
  project?: string;
  totalRatio?: number;
  activeBiasRatio?: number;
  meeting?: number;
  meetingRatio?: number;
}

export interface WorkLifeBalanceConfig {
  lateHour: boolean | number; // Default: 20
  tooManyHours: boolean | number; // Default: 8
  weekendDay: boolean | number[] | string; // Default: '1,7' (Sunday, Saturday)
  outOfShift: boolean | number;
}

export interface WorkLifeBalanceParams {
  from?: string;
  to?: string;
  timezone?: string;
  'group-by'?: string | ('date' | 'userId' | 'currentTagId')[];
  fields?: string | ('totalHours' | 'lastHour' | 'day' | 'tooManyHours' | 'lateHours' |
    'outOfShiftWork' | 'weekendWork' | 'dailyIssues' | 'totalIssues' | 'currentTagId')[];
  resolve?: 'userId' | 'currentTagId';
  user?: string[] | string;
  limit?: number;
  page?: number;
  paging?: 0 | 1;
  'user-tz'?: 0 | 1;
  params?: WorkLifeBalanceConfig;
  period?: 'days';
  interval?: number;
  'post-aggr'?: 'date' | StringLiteral;
  sort?: '_totalIssues' | StringLiteral;
  'result[average]'?: string;
  // API cache time in seconds
  'api-cache'?: number;
}

export interface WorkLifeBalanceResult {
  userId: string;
  totalHours: number;
  lastHour: number;
  day: number[];
  tooManyHours: number;
  lateHours: number;
  outOfShiftWork: number;
  weekendWork: number;
  dailyIssues: number;
  totalIssues: number;
  total: number;
  userName: string;
  currentTagId?: string;
  currentTagName?: string;
  average: number;
  size: number;
}


export interface AverageGroup {
  currentTagId: string;
  currentTagName: string;
  total: number;
  average: number;
  averagePerUser: number;
  averageRatio?: number;
}

export interface PaidLeaveData {
  totalPendingCount: number;
  totalApprovedCount: number;
}

@Injectable({
  providedIn: 'root',
})
export class StatsService {
  readonly defaultLimit = 200;

  constructor(
    protected api: ApiService,
    protected paging: PagingService,
    protected users: UserService,
    protected store: Store,
  ) { }

  getSummaryRatio(options?: GetStatsOptions) {
    const limit = this.getLimit(options);
    return this.getEmpty<SummaryRatio>(options) ?? this.paging.getPagedResult<SummaryRatio>(page => {
      return this.api.request<{ data: SummaryRatio[] }>('get', 'stats/summary', {
        ...this.normalizeOptions(options), page, limit,
      }, null, false, '1.1')
        .pipe(map(result => ({ data: result.data || [] })));
    }, limit, this.getTotalCount(options));
  }

  getSummaryRatioLimitUsers(options?: GetStatsOptions) {
    const limit = this.getLimit(options);
    return this.getEmpty<SummaryRatio>(options) ?? this.paging.getPagedResult<SummaryRatio>(page => {
      return this.api.request<{ data: { users: SummaryRatio[] } }>('get', 'stats/summary-ratio', {
        ...this.limitOptionsUsers(options, page), limit,
      })
        .pipe(map(result => ({ data: result.data.users || [] })));
    }, limit, this.getTotalCount(options) ?? 1);
  }

  getSummary(options?: NewStatsOptions) {
    const limit = this.getLimit(options);
    return this.getEmpty<Summary>(options) ?? this.paging.getPagedResult<Summary>(page => {
      return this.api.request<{ data: Summary[] }>('get', 'stats/summary', {
        ...this.limitOptionsUsers(options, page), limit,
      }, null, true, '1.1')
        .pipe(map(result => ({ data: result.data || [] })));
    }, limit, this.getTotalCount(options) ?? 1);
  }

  getTotal(options?: NewStatsOptions) {
    const limit = this.getLimit(options);
    return this.getEmpty<Summary>(options) ?? this.paging.getPagedResult<Summary>(page => {
      return this.api.request<{ data: Summary[] }>('get', 'stats/total', {
        ...this.limitOptionsUsers(options, page), limit,
      }, null, true, '1.1')
        .pipe(map(result => ({ data: result.data || [] })));
    }, limit, this.getTotalCount(options) ?? 1);
  }

  getPaidLeaveData(options?: NewStatsOptions) {
    return this.api.request<PaidLeaveData>('get', 'work-schedules/stats', {
      ...options,
    }).pipe(map(result => result || null));
  }

  getRawRatio(options?: GetStatsOptions) {
    const limit = this.getLimit(options);
    return this.getEmpty<RawRatio>(options) ?? this.paging.getPagedResult<RawRatio>(page => {
      return this.api.request<{ data: { users: RawRatio[] } }>('get', 'stats/raw-ratio', {
        ...this.normalizeOptions(options), page, limit,
      })
        .pipe(map(result => ({ data: result.data.users || [] })));
    }, limit, this.getTotalCount(options));
  }

  getModeRatio(options?: GetStatsOptions) {
    const limit = this.getLimit(options);
    return this.getEmpty<ModeRatioExtended>(options) ?? this.paging.getPagedResult<ModeRatio>(page => {
      return this.api.request<{ data: ModeRatio[] }>('get', 'stats/total', {
        'group-by': 'userId',
        ...this.normalizeOptions(options), page, limit,
        fields: ['total', 'offcomputer', 'computer', 'mobile', 'manual', 'ratio', 'paidBreak', 'unpaidBreak', MODE_RATIO_LEAVE_FIELDS, 'mobMan', 'userId', 'meeting'].join(','),
      }, {}, false, '1.1')
        .pipe(map(result => ({ data: (result.data || []).map(this.extendModeRatio) })));
    }, limit, this.getTotalCount(options));
  }

  getAverageWorkedGroups(options?: GetStatsOptions) {
    const { timezone, ...filteredOptions } = options;
    const allRegularUsersGroupId = this.store.selectSnapshot(AuthState.company)?.allUsersTagId;
    const limit = this.getLimit(options);
    return this.getEmpty<AverageGroup>(options) ?? this.paging.getPagedResult<AverageGroup>(page => {
      return this.api.request<{ data: AverageGroup[] }>('get', 'stats/total', {
        sort: '_average',
        limit: 10,
        resolve: 'currentTagId',
        period: 'days',
        interval: 1,
        fields: 'currentTagId',
        'bottom-limit': 10,
        'group-by': 'currentTagId,userId,date',
        'postfilter[total]': '300_',
        'result[average]': 'total/size',
        'post-aggr': 'date,userId',
        'user-tz': 1,
        'api-cache': 24 * 60 * 60 * 2,
        'api-cache-ignore': 'from,to,timezone',
        ...this.normalizeOptions(filteredOptions), page,
        from: utcToLocalISO(options.from, options.timezone),
        to: utcToLocalISO(options.to, options.timezone),
      }, {}, false, '1.1')
        .pipe(map(result => {
          const data = result.data || [];
          const total = Math.max(...data.filter(x => !!x).map(item => item?.average));

          return {
            data: data.filter(item => item?.currentTagId !== allRegularUsersGroupId)
              .map(item => item ? ({ ...item, total, averageRatio: item?.average / total } as AverageGroup) : item),
          };
        }));
    }, limit, this.getTotalCount(options));
  }

  private extendModeRatio(val: ModeRatio) {
    const props = ['computer', 'manual', 'mobile', 'offcomputer', 'meeting'];

    const res = { ...val };

    for (const prop of props) {
      res[prop + 'Ratio'] = res[prop] / res.total;
    }
    res['count'] = (res.userId || []).length;

    return res;
  }

  getTimesheetModeRatioDaily(options?: GetStatsOptions) {
    return this.getEmpty<ModeRatio[]>(options) ??
      this.paging.pageByDays(options.from, options.to, (fromDt, toDt) =>
        this.api.request<{ data: ModeRatio[][] }>('get', 'stats/timesheet/total', {
          period: 'days',
          fields: TIMESHEET_MODE_RATIO_FIELDS,
          interval: 1,
          'no-total': 1,
          'group-by': 'company,userId',
          ...this.normalizeOptions(options),
          limit: 5000,
          from: fromDt,
          to: toDt,
        }, null, false, '1.1').pipe(
          map(result => result.data || []),
          map(data => ({ data })),
        ), options.limit, options.timezone);
  }

  getTimesheetRawRatioDaily(options?: GetStatsOptions) {
    return this.getEmpty<RawRatio[]>(options) ??
      this.paging.pageByDays(options.from, options.to, (fromDt, toDt) =>
        this.api.request<{ data: { users: RawRatio[] }[] }>('get', 'stats/timesheet/raw-ratio', {
          period: 'days',
          interval: 1,
          'no-total': 1,
          ...this.normalizeOptions(options),
          limit: 5000,
          from: fromDt,
          to: toDt,
        }).pipe(
          map(result => result.data.map(x => x.users) || []),
          map(data => ({ data })),
        ), options.limit, options.timezone);
  }

  getTimesheetSummaryRatioDaily(options?: GetStatsOptions) {
    return this.getEmpty<SummaryRatio[]>(options) ??
      this.paging.pageByDays(options.from, options.to, (fromDt, toDt) =>
        this.api.request<{ data: SummaryRatio[][] }>('get', 'stats/timesheet/summary', {
          fields: 'idleSec,idleMins,userId,total,idleMinsRatio,idleSecRatio,prod,prodRatio,unprod,unprodRatio,manual,manualRatio,mobile,mobileRatio,keysPerNonIdleMin',
          sort: 'date',
          period: 'days',
          interval: 1,
          'no-total': 1,
          'group-by': 'company,userId',
          ...this.normalizeOptions(options),
          limit: 5000,
          from: fromDt,
          to: toDt,
        }, null, false, '1.1').pipe(
          map(result => result.data || []),
          map(data => ({ data })),
        ), options.limit, options.timezone);
  }

  getTimesheetWorkRangeDaily(options: GetStatsOptions) {
    const limit = this.getLimit(options);
    return this.getEmpty<WorkRange[]>(options) ??
      this.paging.getPagedResult<WorkRange[]>(page => {
        return this.api.request<{ data: WorkRange[][] }>('get', 'stats/timesheet/total', {
          period: 'days',
          interval: 1,
          ...this.limitOptionsUsers(options, page),
        }, {}, false, '1.1', 1)
          .pipe(map(result => ({ data: result.data || [] })));
      }, limit, this.getTotalCount(options) ?? 1);
  }

  getTimesheetTasks(options?: GetStatsOptions) {
    const limit = this.getLimit(options);
    return this.paging.getPagedResult(page => {
      return this.api.request<{ data: { task: StatsTotal[], paging: PagingObject }[] }>('get', 'stats/timesheet/task-total', {
        ...this.normalizeOptions(options), page, limit,
      }).pipe(
        map(result => {
          const results: PagedApiResult<StatsTotal> = { data: [] };
          result.data.forEach((day, index) => {
            const date = DateTime.fromISO(options.from, { zone: options.timezone }).plus({ days: index })
              .toLocaleString(DateTime.DATE_SHORT);

            results.data = results.data.concat(
              day.task.map(t => {
                t.date = date;
                return t;
              }),
            );
            if (!results.paging || (results.paging && day.paging && results.paging.totalCount < day.paging.totalCount)) {
              results.paging = day.paging;
            }
          });
          return results;
        }));
    }, limit);
  }

  getProjectsTotal(options?: GetStatsOptions) {
    const limit = this.getLimit(options);
    return this.getEmpty<StatsTotal>(options) ?? this.paging.getPagedResult<StatsTotal>(page => {
      return this.api.request<{ data: { project: StatsTotal[] } }>('get', 'stats/project-total', {
        ...this.normalizeOptions(options), page, limit,
      })
        .pipe(map(result => ({ data: result.data.project })));
    }, limit);
  }

  getTasksTotal(options?: GetStatsOptions) {
    const limit = this.getLimit(options);
    return this.getEmpty<StatsTotal>(options) ?? this.paging.getPagedResult<StatsTotal>(page => {
      return this.api.request<{ data: { task: StatsTotal[], paging } }>('get', 'stats/task-total', {
        ...this.normalizeOptions(options), page, limit,
      })
        .pipe(map(result => ({ data: result.data.task.map(task => ({ ...task, pagingTotalCount: result.data.paging.totalCount })) })));
    }, limit);
  }

  getWorkRange(options?: GetStatsOptions) {
    const limit = this.getLimit(options);
    return this.getEmpty<WorkRange>(options) ??
      this.paging.getPagedResult<WorkRange>(page => {
        return this.api.request<{ data: WorkRange[] }>('get', 'stats/total', {
          ...this.limitOptionsUsers(options, page),
        }, {}, false, '1.1', 200)
          .pipe(map(result => ({ data: result.data || [] })));
      }, limit, this.getTotalCount(options) ?? 1);
  }

  private getLimit(options: { limit?: number }): number {
    return options?.limit || this.defaultLimit;
  }

  private getTotalCount(options: { user?: string | string[] }): number {
    return Array.isArray(options?.user) ? options.user.length : undefined;
  }

  private isEmpty(options: { user?: string | string[] }) {
    return this.getTotalCount(options) === 0;
  }

  protected getEmpty<T>(options: { user?: string | string[] }) {
    if (this.isEmpty(options)) {
      return this.paging.empty<T>();
    }
    return null;
  }

  normalizeOptions(options: GetStatsOptions): GetStatsOptionsNormalized {
    return { ...(options || {}), ...(options.user && { user: options.user + '' }), ...(options.tag && { tag: options.tag + '' }) };
  }

  private limitOptionsUsers(options: GetStatsOptions, page: number): GetStatsOptionsNormalized {
    const limit = this.defaultLimit;
    const users = this.limitPageUsers(options?.user, limit, page);
    const user = Array.isArray(users) ? users.join(',') : users;
    return this.normalizeOptions({ ...(options || {}), user });
  }

  private limitPageUsers(users: StatsUserFilter, limit: number, page: number): StatsUserFilter {
    if (!Array.isArray(users)) { return users; }
    return users.slice(page, page + limit);
  }

  public newStats(options: NewStatsOptions & { 'group-by': 'comCat,score' | 'comCat,userId' }): PagedResult<NewStatsComcatResult>;
  public newStats(options: NewStatsOptions & { 'group-by': 'company' }): PagedResult<NewStatsCompanyResult>;
  public newStats(options: NewStatsOptions & { 'group-by': 'userId' | 'currentTagId' }): PagedResult<NewStatsUserResult>;
  public newStats<T>(options: NewStatsOptions): PagedResult<T> {
    const limit = this.getLimit(options);
    return this.getEmpty<T>(options) ?? this.paging.getPagedResult<T>(page => {
      return this.api.request<{ data: T[] }>('get', 'stats/category', {
        ...this.normalizeOptions(options), page, limit,
      }, null, true, '1.1');
    }, limit);
  }

  public newStatsTotal<T>(options: NewStatsOptions): PagedResult<T> {
    const limit = this.getLimit(options);
    const datePaging = options['date-paging'] === 1;
    const { page: pg, limit: lm, 'paging-fields': pagingFields, ...normalized } = this.normalizeOptions(options);

    return this.getEmpty<T>(options) ?? this.paging.getPagedResult<T>(page => {
      return this.api.request<{ data: T[] }>('get', 'stats/total', {
        ...normalized,
        ...datePaging ? {
          'date-page': page,
          'date-limit': limit,
        } : {
          page,
          limit,
        },
        ...(!page && pagingFields) ? { 'paging-fields': pagingFields } : {},
        ...page > 0 && { 'no-total': 1 },
      }, null, true, '1.1');
    }, limit);
  }

  public newTimesheetTotal<T>(options: NewStatsOptions): PagedResult<T> {
    const limit = options['date-limit'] || this.getLimit(options);
    const dr = DateRange.fromOptions(options);
    const divided = dr.setTimezone(options.timezone || dr.timezone)
      .divide(
        Duration.fromObject({ [options.period || 'days']: options.interval }),
        options.period || true,
      );

    return this.getEmpty<T>(options) ?? this.paging.getPagedResult<T>(page => {
      return this.api.request<{ data: T[] }>('get', 'stats/timesheet/total', {
        ...this.normalizeOptions(options),
        'date-page': page,
        'date-limit': limit,
      }, null, true, '1.1');
    }, limit, divided.length);
  }

  public newStatsByUsers(options: NewStatsOptions & { 'group-by': 'comCat,score' | 'comCat,userId' }): PagedResult<NewStatsComcatResult>;
  public newStatsByUsers(options: NewStatsOptions & { 'group-by': 'company' }): PagedResult<NewStatsCompanyResult>;
  public newStatsByUsers(options: NewStatsOptions & { 'group-by': 'userId' | 'currentTagId' }): PagedResult<NewStatsUserResult>;
  public newStatsByUsers<T extends {}>(options: NewStatsOptions): PagedResult<T> {
    const limit = this.getLimit(options);

    const { user, tag, sort, ...rest } = options;

    return this.getEmpty<T>(options) ?? this.paging.getPagedResult<T>(page => {
      return from(this.users.users({
        ...user && user !== 'all-on-reports' && user !== 'all-self-on-reports' && { 'filter[id]': user },
        ...tag && { tag },
        sort, limit, page,
        detail: 'alias',
        'filter[show-on-reports]': 1,
      }).raw().then(userRes =>
        lastValueFrom(
          this.api.request<{ data: T[] }>('get', 'stats/category', {
            ...this.normalizeOptions(rest), user: userRes.data.map(u => u.id).join(),
          }, null, false, '1.1'),
        ).then(statsRes => {
          const statsMap = new Map(statsRes.data.map(dt => [dt['userId'], dt]));
          return { data: userRes.data.map(u => ({ ...statsMap.get(u.id), userId: u.id, userName: u.name })) };
        }),
      ));
    }, limit);
  }

  public newStatsTimesheet(options: NewStatsOptions & { 'group-by'?: 'company' }): PagedResult<NewStatsCompanyResult[]>;
  public newStatsTimesheet(options: NewStatsOptions & { 'group-by': 'userId' }): PagedResult<NewStatsUserResult[]>;
  public newStatsTimesheet<T>(options: NewStatsOptions): PagedResult<T> {
    return this.paging.pageByDays<T>(options.from, options.to, (fromDate, toDate) => {
      if (this.isEmpty(options)) { return of({ data: Array(options.limit).fill([]) }); }
      return this.api.request<{ data: T[] }>('get', 'stats/timesheet/category-total', {
        period: 'days',
        interval: 1,
        'no-total': 1,
        limit: 1,
        ...this.normalizeOptions(options),
        from: fromDate,
        to: toDate,
      }, null, false, '1.1');
    }, options.limit, options.timezone);
  }

  withErrorHandling<T>(emptyCallback: T = null, errorFallback: T | false = false): OperatorFunction<T, T> {
    return catchError<T, Observable<T>>(err => {
      if (err.error === 'denied' && err.reason === 'noAllowedUsers') {
        return of(emptyCallback);
      }
      if (errorFallback === false) { return throwError(() => err); }
      return of(errorFallback);
    });
  }

  getWorkLifeConfig(cfg?: WorkLife): WorkLifeBalanceConfig {
    const companySettings = this.store.selectSnapshot(AuthState.company)?.companySettings;
    const config = cfg || companySettings?.worklife;

    const fdow = this.store.selectSnapshot(AuthState.firstDayOfWeek) + 1;

    const weekends = [((fdow + 5) % 7) || 7, ((fdow + 6)) || 7];

    return {
      lateHour: config?.lateHoursAfter?.included ? config.lateHoursAfter.value || 20 : false,
      tooManyHours: config?.tooManyHours?.included ? config.tooManyHours.value || 9 : false,
      weekendDay: config?.weekendWork?.included ? weekends.join() : false,
      outOfShift: companySettings?.workScheduleFeature ?
        (config?.hoursOutsideShiftWork?.included ? config?.hoursOutsideShiftWork.value : false) : false,
    };
  }

  getWorkLifeFields(cfg: WorkLifeBalanceConfig, fields: string): string {
    const fieldsArr = fields.split(',');

    return fieldsArr.filter(x =>
      (x !== 'weekendWork' || cfg.weekendDay !== false) &&
      (x !== 'lateHours' || cfg.lateHour !== false) &&
      (x !== 'tooManyHours' || cfg.tooManyHours !== false) &&
      (x !== 'outOfShiftWork' || cfg.outOfShift !== false),
    ).join();
  }

  workLifeBalance(options: WorkLifeBalanceParams = {}, allowWlConfigParams = true) {
    const limit = this.getLimit(options);
    const params = options.params ?? this.getWorkLifeConfig();
    const fields = this.getWorkLifeFields(params, options.fields + '');

    return this.getEmpty<WorkLifeBalanceResult>(options) ?? this.paging.getPagedResult<WorkLifeBalanceResult>(page => {
      return this.api.request('get', 'stats/work-life', {
        ...options,
        limit,
        page,
        'group-by': options['group-by'] + '',
        fields,
        ...allowWlConfigParams && {
          'params[lateHour]': params.lateHour,
          'params[tooManyHours]': params.tooManyHours,
          'params[weekendDay]': params.weekendDay,
          'params[outShiftHours]': params.outOfShift,
        },
      } as WorkLifeBalanceParams, {}, false, '1.1');
    }, limit);
  }

  getUnratedAppCount() {
    return this.api.request<{ data: { unratedCatsCount: number } }>('get', 'categories/unrated-count').pipe(map(resp => resp.data.unratedCatsCount));
  }
}
